import Vue from "vue";
import Vuex from "vuex";

import i18n from "./messages/i18n";
import router from "./router";
import { ROUTE_HOME, ROUTE_LOGIN, FIREBASE_API_KEY } from "./constants";

Vue.use(Vuex);

let logoutTimerInterval;

const storeConfig = {
  state: {
    backgroundColor: "ffffff",
    data: {
      categories: {},
      food: {}
    },
    user: null, // TODO: set app language based on user's preference
    loginExpiresIn: 0,
    resources: {
      users: null,
      pantries: null,
      categories: null,
      food: null,
      auth: null
    },
    searchQuery: ""
  },

  getters: {
    currentLanguage: () => {
      return i18n.locale;
    },
    categories: state => {
      return state.data.categories;
    },
    categoryById: state => id => {
      if (state.data.categories.hasOwnProperty(id)) {
        return state.data.categories[id];
      }
      return {};
    },
    food: state => {
      return state.data.food;
    },
    foodById: state => id => {
      if (state.data.food.hasOwnProperty(id)) {
        return state.data.food[id];
      }
      return {};
    },
    foodInCategory: state => id => {
      let filtered_food = {};
      for (const [food_key, food_item] of Object.entries(state.data.food)) {
        if (food_item.categories.includes(id)) {
          filtered_food[food_key] = food_item;
        }
      }
      return filtered_food;
    },
    amountInCategory: state => id => {
      let amount = 0;
      for (const food_item of Object.values(state.data.food)) {
        if (food_item.categories.includes(id)) {
          amount += food_item.amount;
        }
      }
      return amount;
    },
    loggedUser: state => {
      return (
        state.user && new Date().getTime() < parseInt(state.user.expiresAt)
      );
    },
    userAuthData: (state, getters) => {
      return {
        userId: state.user ? state.user.userId : null,
        authToken: state.user ? state.user.authToken : null
      };
    },
    pantryAuthData: (state, getters) => {
      return {
        pantryId: state.user ? state.user.pantryId : null,
        authToken: state.user ? state.user.authToken : null
      };
    },
    searchQuery: state => {
      return state.searchQuery;
    }
  },

  mutations: {
    changeLanguage: (state, locale) => {
      localStorage.setItem("lang", locale);
      i18n.setLocale(locale);
    },
    changeBg: (state, hexcolor) => {
      hexcolor = hexcolor ? hexcolor : "ffffff";
      state.backgroundColor = hexcolor;
    },
    updateCategories: (state, categories) => {
      state.data.categories = categories;
      console.log("categories object in store updated");
    },
    updateFood: (state, food) => {
      // fix empty categories array NOT saved in firebase
      for (const food_item of Object.values(food)) {
        if (!food_item.hasOwnProperty("categories")) {
          food_item.categories = [];
        }
      }
      state.data.food = food;
      console.log("food object in store updated");
    },
    loginUser: (state, payload) => {
      state.user = {
        userId: payload.login_response.localId,
        authToken: payload.login_response.idToken,
        refreshToken: payload.login_response.refreshToken,
        expiresAt:
          new Date().getTime() + payload.login_response.expiresIn * 1000,
        pantryId: payload.pantryId
      };
      console.log("user logged", payload.login_response);

      // redirect to (personalized) HOME
      router.push({
        name: ROUTE_HOME
      });
    },
    logoutUser: state => {
      // logout
      clearInterval(logoutTimerInterval);
      state.loginExpiresIn = 0;
      state.user = null;
      // reset user related data
      state.backgroundColor = "ffffff";
      state.data = {
        categories: {},
        food: {}
      };
      // redirect to login
      router.push({
        name: ROUTE_LOGIN
      });
    },
    refreshUser: (state, refresh_response) => {
      state.user = {
        userId: refresh_response.user_id,
        authToken: refresh_response.id_token,
        refreshToken: refresh_response.refresh_token,
        expiresAt: new Date().getTime() + refresh_response.expires_in * 1000
      };
      console.log("user refreshed", refresh_response);
    },
    search: (state, query) => {
      state.searchQuery = query;
    },
    clearSearch: state => {
      state.searchQuery = "";
    }
  },

  actions: {
    fetchPantry: context => {
      context.state.resources.pantries.get(context.getters.pantryAuthData).then(
        response => {
          context.commit(
            "updateCategories",
            response.body &&
              response.body.hasOwnProperty("categories") &&
              response.body.categories
              ? response.body.categories
              : {}
          );
          context.commit(
            "updateFood",
            response.body &&
              response.body.hasOwnProperty("food") &&
              response.body.food
              ? response.body.food
              : {}
          );
        },
        error => {
          console.log(error);
        }
      );
    },
    fetchCategories: context => {
      context.state.resources.categories
        .get(context.getters.pantryAuthData)
        .then(
          response => {
            context.commit(
              "updateCategories",
              response.body ? response.body : {}
            );
          },
          error => {
            console.log(error);
          }
        );
    },
    fetchFood: context => {
      context.state.resources.food.get(context.getters.pantryAuthData).then(
        response => {
          context.commit("updateFood", response.body ? response.body : {});
        },
        error => {
          console.log(error);
        }
      );
    },
    login: (context, credentials) => {
      context.state.resources.auth
        .save(
          // this will fire POST request
          {
            endpoint: "signInWithPassword"
          },
          {
            email: credentials.email,
            password: credentials.password,
            returnSecureToken: true
          }
        )
        .then(
          response =>
            context.dispatch("handleLoginSuccess", {
              response
            }),
          error => context.dispatch("handleLoginError", error)
        );
    },
    register: (context, credentials) => {
      context.state.resources.auth
        .save(
          // this will fire POST request
          {
            endpoint: "signUp"
          },
          {
            email: credentials.email,
            password: credentials.password,
            returnSecureToken: true
          }
        )
        .then(
          response =>
            context.dispatch("handleLoginSuccess", {
              response,
              pantryId: credentials.pantryId
            }),
          error => context.dispatch("handleLoginError", error)
        );
    },
    refreshToken: context => {
      Vue.http
        .post(
          "https://securetoken.googleapis.com/v1/token?key=" + FIREBASE_API_KEY,
          {
            grant_type: "refresh_token",
            refresh_token: context.state.user.refreshToken
          }
        )
        .then(
          response => {
            context.commit("refreshUser", response.body);
            console.log(response.body);
          },
          response => {
            // error callback
          }
        );
    },
    initUser: context => {
      context.dispatch("logoutTimer");
      // get user's pantry ID - for new registration or logged user this should be already in state.user object
      if (context.state.user.pantryId) {
        // load user's pantry immediatelly
        context.dispatch("fetchPantry");
      } else {
        // load user node from database and get Pantry ID
        context.state.resources.users.get(context.getters.userAuthData).then(
          response => {
            // update pantryId in state user object and load data for user
            const pantryId =
              response.body && response.body.hasOwnProperty("pantryId")
                ? response.body.pantryId
                : null;
            context.state.user.pantryId = pantryId;
            console.log(pantryId, "loaded pantryId");

            // load user's pantry once we know the ID
            context.dispatch("fetchPantry");
          },
          error => {
            console.log(error);
          }
        );
      }
    },
    checkLoggedUser(context) {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user) {
        // Replace the state object with the stored item
        this.replaceState(
          Object.assign(context.state, {
            user
          })
        );
        context.dispatch("initUser");
      }
    },
    logoutTimer: context => {
      logoutTimerInterval = setInterval(() => {
        const distance = context.getters.loggedUser
          ? context.state.user.expiresAt - new Date().getTime()
          : -1;
        context.state.loginExpiresIn = Math.max(0, distance);

        // If the time expired, logout user
        if (distance < 0) {
          context.commit("logoutUser");
        }
      }, 1000);
    },
    handleLoginSuccess: (context, payload) => {
      if (
        payload.response.hasOwnProperty("body") &&
        payload.response.body.hasOwnProperty("localId")
      ) {
        // SUCCESSFULLY AUTHENTICATED
        console.log(payload.response);
        const registrationPantryId = payload.hasOwnProperty("pantryId")
          ? payload.pantryId
          : null;
        context.commit("loginUser", {
          login_response: payload.response.body,
          pantryId: registrationPantryId
        });

        if (registrationPantryId) {
          // if pantryId is present in payload, this is new user - save pantry ID / create new user node in database
          context.state.resources.users
            .update(context.getters.userAuthData, {
              pantryId: registrationPantryId
            })
            .then(
              response => {
                // update pantryId in state user object and load data for user
                console.log(
                  response.body.pantryId,
                  "new user created with pantryId"
                );
              },
              error => {
                console.log(error);
              }
            );
        }

        // load user's data in parallel
        context.dispatch("initUser");
      } else {
        // AUTHENTICATION ERROR
        context.dispatch("handleLoginError", payload.response);
      }
    },
    handleLoginError: (context, response) => {
      if (
        response.hasOwnProperty("body") &&
        response.body.hasOwnProperty("error") &&
        response.body.error.hasOwnProperty("message")
      ) {
        switch (
          response.body.error.message // TODO: react to the error by showing error in the form validation
        ) {
          // registration errors
          case "EMAIL_EXISTS":
            console.log("EMAIL_EXISTS");
            break;
          case "TOO_MANY_ATTEMPTS_TRY_LATER":
            console.log("Dude, calm down!");
            break;
          // login errors
          case "EMAIL_NOT_FOUND":
            console.log("EMAIL_NOT_FOUND");
            break;
          case "INVALID_PASSWORD":
            console.log("INVALID_PASSWORD");
            break;
          case "USER_DISABLED":
            console.log("Sorry baby, but you are a bitch!");
            break;
          // unknown errors
          default:
            console.log(response.body.error);
        }
      } else {
        // WTF?
        console.log(response);
      }
    }
  }
};

const store = new Vuex.Store(storeConfig);

const saveUserInLS = user => localStorage.setItem("user", JSON.stringify(user));

// Subscribe to store updates
store.subscribe((mutation, state) => {
  // Store the state user object as a JSON string in localStorage...but only for relevant mutations
  if (["loginUser", "logoutUser", "refreshUser"].indexOf(mutation.type) > -1) {
    saveUserInLS(state.user);
  }
});

// Subscribe to store actions updates
store.subscribeAction((action, state) => {
  // Store the state user object as a JSON string in localStorage...but only for relevant actions
  if (["fetchPantry"].indexOf(action.type) > -1) {
    saveUserInLS(state.user);
  }
});

export default store;
